'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { resultsContext } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { selectLastResult } from '#pplx/frontend/apps/ask/providers/ResultsProviderShared.js';
import { socialInfoToStats } from '#pplx/frontend/apps/ask/thread-data/rwTokenUtils.js';
import { shallowEqual } from '#pplx/frontend/common/shallowEqual.js';
import { MessageHelper } from '#pplx/frontend/components/utils/MessageHelper.js';
import { useStreamStore } from '#pplx/frontend/stream/stream.js';
import { useDerivedHooks } from '#pplx/frontend/zustand-context/createZustandContext.js';
import { first } from 'lodash-es';
import React, { useCallback, useMemo, useRef, useState } from 'react';
function createResultsProviderState(resetKey) {
    return {
        authoritativeFirstResult: undefined,
        authoritativeLastResult: undefined,
        resetKey,
        streamId: undefined,
        threadId: undefined,
        threadMetadata: null,
    };
}
function deriveResultsFromStreamStore({ entries, threads, streams, currentStreamId, currentThreadId, }) {
    const currentStream = currentStreamId ? streams[currentStreamId] : undefined;
    const effectiveThreadId = currentStream?.threadId ?? currentThreadId;
    if (effectiveThreadId) {
        const thread = threads.find((t) => t.id === effectiveThreadId);
        if (thread) {
            const results = thread.entryIds.map((id) => entries[id]).filter((v) => v != null);
            // Stream just started and not yet bound to an entry - use placeholder
            if (currentStream?.placeholderChunk && !currentStream.entryId) {
                results.push(currentStream.placeholderChunk);
            }
            return results;
        }
    }
    return currentStream?.placeholderChunk
        ? [currentStream.placeholderChunk]
        : [];
}
function resolveAuthoritativeResult(visibleResults, authoritativeResult, effectiveThreadId, position) {
    const loadedResult = position === 'first' ? first(visibleResults) : selectLastResult(visibleResults);
    if (!authoritativeResult || authoritativeResult.context_uuid !== effectiveThreadId) {
        return loadedResult;
    }
    return (visibleResults.find((result) => result.backend_uuid === authoritativeResult.backend_uuid) ??
        authoritativeResult);
}
export const ResultsProvider = ({ children, resetKey, holdOnClear = false }) => {
    // Read during the commit that hides the page, so it is assigned while rendering.
    const holdOnClearRef = useRef(holdOnClear);
    holdOnClearRef.current = holdOnClear;
    const streamStore = useStreamStore();
    const [providerState, setProviderState] = useState(() => createResultsProviderState(resetKey));
    const activeProviderState = providerState.resetKey === resetKey ? providerState : createResultsProviderState(resetKey);
    const { authoritativeFirstResult, authoritativeLastResult, streamId, threadId, threadMetadata } = activeProviderState;
    const updateProviderState = useCallback((updater) => {
        setProviderState((current) => updater(current.resetKey === resetKey ? current : createResultsProviderState(resetKey)));
    }, [resetKey]);
    // When setting threadId, also update streamId based on matching stream
    const setCurrentThreadId = useCallback((id) => {
        updateProviderState((current) => {
            const stream = id
                ? Object.values(streamStore.getState().streams).find((item) => item.threadId === id)
                : undefined;
            return {
                ...current,
                authoritativeFirstResult: current.authoritativeFirstResult?.context_uuid === id
                    ? current.authoritativeFirstResult
                    : undefined,
                authoritativeLastResult: current.authoritativeLastResult?.context_uuid === id
                    ? current.authoritativeLastResult
                    : undefined,
                streamId: id ? stream?.id.description : current.streamId,
                threadId: id,
            };
        });
    }, [streamStore, updateProviderState]);
    // When setting streamId, derive threadId from the stream if available
    const setCurrentStreamId = useCallback((id) => {
        updateProviderState((current) => {
            const stream = id ? streamStore.getState().streams[id] : undefined;
            return {
                ...current,
                authoritativeFirstResult: id ? undefined : current.authoritativeFirstResult,
                authoritativeLastResult: id ? undefined : current.authoritativeLastResult,
                streamId: id,
                threadId: stream?.threadId ?? current.threadId,
            };
        });
    }, [streamStore, updateProviderState]);
    const setAuthoritativeFirstResult = useCallback((result) => {
        updateProviderState((current) => ({ ...current, authoritativeFirstResult: result }));
    }, [updateProviderState]);
    const setAuthoritativeLastResult = useCallback((result) => {
        updateProviderState((current) => ({ ...current, authoritativeLastResult: result }));
    }, [updateProviderState]);
    const setThreadMetadata = useCallback((metadata) => {
        updateProviderState((current) => ({ ...current, threadMetadata: metadata }));
    }, [updateProviderState]);
    const clear = useCallback(() => {
        if (holdOnClearRef.current)
            return;
        setProviderState(createResultsProviderState(resetKey));
    }, [resetKey]);
    const actions = useMemo(() => ({
        setCurrentThreadId,
        setCurrentStreamId,
        setAuthoritativeFirstResult,
        setAuthoritativeLastResult,
        setThreadMetadata,
        clear,
    }), [
        clear,
        setAuthoritativeFirstResult,
        setAuthoritativeLastResult,
        setCurrentStreamId,
        setCurrentThreadId,
        setThreadMetadata,
    ]);
    const derive = useMemo(() => {
        let previous;
        return (state) => {
            const allResults = deriveResultsFromStreamStore({
                entries: state.entries,
                threads: state.threads,
                streams: state.streams,
                currentStreamId: streamId,
                currentThreadId: threadId,
            });
            const visibleResults = allResults.filter((item) => {
                if (item.is_background)
                    return false;
                // Filter out empty completed entries (e.g. cancelled cron wake-ups that
                // produced no content). These have no query, no workflow, and no plan.
                const isCompleted = item.status?.toLowerCase() === 'completed';
                const hasNoQuery = !item.query_str;
                const hasNoContent = !item.blocks?.some((b) => b.workflow_block?.steps?.length || b.plan_block?.goals?.length);
                if (isCompleted && hasNoQuery && hasNoContent)
                    return false;
                return true;
            });
            const backgroundEntries = allResults.filter((item) => item.is_background);
            const effectiveThreadId = (streamId ? state.streams[streamId]?.threadId : undefined) ?? threadId;
            const firstResult = resolveAuthoritativeResult(visibleResults, authoritativeFirstResult, effectiveThreadId, 'first');
            const lastResult = resolveAuthoritativeResult(visibleResults, authoritativeLastResult, effectiveThreadId, 'last');
            const inFlightEntry = lastResult && MessageHelper.isStatusPending(lastResult)
                ? lastResult
                : undefined;
            const hasRunningBackgroundEntries = backgroundEntries.some((entry) => MessageHelper.isStatusPending(entry));
            const next = {
                results: visibleResults,
                backgroundEntries,
                firstResult,
                lastResult,
                inFlightEntry,
                inFlight: !!inFlightEntry,
                inFlightLatest: !!inFlightEntry,
                hasRunningBackgroundEntries,
                socialStats: socialInfoToStats(firstResult?.social_info),
                resultsLength: visibleResults.length,
            };
            // Other threads also update the stream store. Preserve references so their
            // updates do not rerender consumers of this thread's derived results.
            if (previous) {
                if (shallowEqual(previous.results, next.results))
                    next.results = previous.results;
                if (shallowEqual(previous.backgroundEntries, next.backgroundEntries)) {
                    next.backgroundEntries = previous.backgroundEntries;
                }
                if (shallowEqual(previous.socialStats, next.socialStats)) {
                    next.socialStats = previous.socialStats;
                }
            }
            previous = previous && shallowEqual(previous, next) ? previous : next;
            return previous;
        };
    }, [authoritativeFirstResult, authoritativeLastResult, threadId, streamId]);
    const hooks = useDerivedHooks(streamStore, derive);
    // Reads the store on demand so callers don't re-render while answers stream.
    const getLoadedResults = useCallback(() => {
        const { results, firstResult } = derive(streamStore.getState());
        return firstResult && !results.includes(firstResult) ? [firstResult, ...results] : results;
    }, [derive, streamStore]);
    const value = useMemo(() => ({
        ...hooks,
        actions,
        currentThreadId: threadId,
        currentStreamId: streamId,
        threadMetadata,
        getLoadedResults,
    }), [threadId, streamId, hooks, actions, threadMetadata, getLoadedResults]);
    return _jsx(resultsContext.Provider, { value: value, children: children });
};
//# sourceMappingURL=ResultsProvider.js.map